// Hook: useMakeReservation.js (wersja z własnymi modalami)
import { useCallback } from "react";
import axios from "../../api/axiosInstance";
import { isAffectingNextWorkingDay, fixLocalDate } from "../../utils/dateHelpers";
import { findBestSpotAssignment } from "../../utils/findBestSpotAssignment";
import showModal from "../../components/ui/modals/showModal"; // Twój własny modal confirm/info (do stworzenia)
import { formatDateList } from "../../utils/formatDateList";
import { useNavigate } from "react-router-dom";

function useMakeReservation({
  userId,
  selectedDates,
  selectedSpot,
  regCarNum,
  baseUrl,
  reserveBtnRef,
  userReservedDates,
  refetchUserReservations,
  refetchNextReservationInfo,
  setCalendarKey,
  lightReset,
  autoAssignEnabled
}) {
  const navigate = useNavigate(); 
  const token = localStorage.getItem("token");

  const makeManualReservation = async () => {
    if (!selectedSpot) {
      showModal("Wybierz miejsce parkingowe.", { type: "error" });
      return;
    }

    const today = fixLocalDate(new Date()).toISOString().split("T")[0];
    const dateStrings = selectedDates.map(d => d.toISOString().split("T")[0]);
    const pastDates = dateStrings.filter(d => d < today);
    const futureDates = dateStrings.filter(d => d >= today);

    if (pastDates.length === dateStrings.length) {
      showModal("Nie można rezerwować wyłącznie dat wstecz.", { type: "error" });
      return;
    }

    if (pastDates.length > 0) {
      const readable = formatDateList(pastDates);
      const confirmed = await showModal(
        `Nie można rezerwować dat wstecz:\n${readable}\n\nCzy chcesz kontynuować z pozostałymi (${futureDates.length}) dniami?`,
        { type: "confirm" }
      );
      if (!confirmed) return;
    }

    const spotReservations = selectedSpot.reservations || [];
    const datesTakenByOthers = futureDates.filter(date =>
      spotReservations.some(r => r.date === date && r.userId !== userId)
    );
    const datesAlreadyMine = futureDates.filter(date => userReservedDates.includes(date));
    const availableDates = futureDates.filter(
      date => !datesTakenByOthers.includes(date) && !datesAlreadyMine.includes(date)
    );
    const info = [
      pastDates.length ? `⏳ Wstecz: ${formatDateList(pastDates)}` : null,
      datesAlreadyMine.length ? `🔵 Masz już: ${formatDateList(datesAlreadyMine)}` : null
    ].filter(Boolean).join("\n");
    
    if (availableDates.length === 0) {
      await showModal(`Nie można zarezerwować żadnego z wybranych dni.\n\n${info}`, { type: "error" });
      return;
    }

    if (availableDates.length < futureDates.length) {
      const info = [
        datesAlreadyMine.length ? `🔵 Masz już: ${formatDateList(datesAlreadyMine)}` : null,
        datesTakenByOthers.length ? `🔴 Zajęte: ${formatDateList(datesTakenByOthers)}` : null,
        availableDates.length ? `🟢 Dostępne: ${formatDateList(availableDates)}` : null
      ].filter(Boolean).join("\n");

      const confirmed = await showModal(
        `Nie wszystkie dni są dostępne.\n\n${info}\n\nZarezerwować tylko dostępne (${availableDates.length}) dni?`,
        { type: "confirm" }
      );
      if (!confirmed) return;
    }

    await axios.post(`${baseUrl}/api/reservations`, {
      userId,
      parkingSpotId: selectedSpot.id,
      dates: availableDates,
      regCarNum
    });

    showModal(`✅ Zarezerwowano miejsce #${selectedSpot.spotNumber} na ${formatDateList(availableDates)}`, { type: "success" });

    setCalendarKey(prev => prev + 1);
    refetchUserReservations();
    if (isAffectingNextWorkingDay(selectedDates)) {
      refetchNextReservationInfo();
    }
    lightReset();
  };

  const makeAutoReservation = async () => {
    const today = fixLocalDate(new Date()).toISOString().split("T")[0];
    const dateStrings = selectedDates.map(d => d.toISOString().split("T")[0]);
  
    // Podział dat na przeszłe i przyszłe
    const pastDates = dateStrings.filter(d => d < today);
    const futureDates = dateStrings.filter(d => d >= today);
  
    // Dni, na które użytkownik już ma rezerwację
    const datesAlreadyMine = futureDates.filter(date =>
      userReservedDates.includes(date)
    );
  
    // Dni, które można potencjalnie zarezerwować
    const availableDates = futureDates.filter(
      date => !datesAlreadyMine.includes(date)
    );
  
    // 🧱 1. Walidacja: wszystkie daty są w przeszłości
    if (pastDates.length === dateStrings.length) {
      showModal("Nie można rezerwować wyłącznie dat wstecz.", { type: "error" });
      return;
    }
  
    // 🧱 2. Walidacja: wszystkie dni już masz zarezerwowane
    if (datesAlreadyMine.length === futureDates.length) {
      showModal("🔵 Masz już rezerwacje na wszystkie wybrane dni.", { type: "info" });
      return;
    }
  
    // 🧱 3. Walidacja: nie ma żadnych dni możliwych do przydziału
    if (availableDates.length === 0) {
      await showModal(
        "❌ Wszystkie dni są już zajęte lub masz na nie rezerwację.",
        { type: "error" }
      );
      return;
    }
  
    // 🔄 Pytamy, czy kontynuować tylko z przyszłymi datami (jeśli były wsteczne)
    if (pastDates.length > 0) {
      const readable = formatDateList(pastDates);
      const confirmed = await showModal(
        `❌ Nie można rezerwować dat wstecz:\n${readable}\n\n✅ Czy chcesz kontynuować z pozostałymi (${availableDates.length}) dniami?`,
        { type: "confirm" }
      );
      if (!confirmed) return;
    }
  
    // 📦 4. Znajdź możliwe przypisania miejsc
    const assignments = await findBestSpotAssignment(
      availableDates.map(d => new Date(d)),
      baseUrl
    );
  
    // 🔁 5. Przypisane i nieprzypisane daty
    const assignedDates = Object.values(assignments).flatMap(a => a.dates);
    const unavailableDates = availableDates.filter(d => !assignedDates.includes(d));
  
    // 🔁 6. Pokazujemy tylko wtedy, gdy są jakieś dni NIEprzypisane
    if (unavailableDates.length > 0) {
      const info = [
        unavailableDates.length ? `🔴 Brak miejsc: ${formatDateList(unavailableDates)}` : null,
        assignedDates.length ? `🟢 Dostępne: ${formatDateList(assignedDates)}` : null,
        datesAlreadyMine.length ? `🔵 Już masz: ${formatDateList(datesAlreadyMine)}` : null
      ].filter(Boolean).join('\n');
  
      const confirmed = await showModal(
        `Nie wszystkie dni są dostępne:\n\n${info}\n\nZarezerwować tylko dostępne dni?`,
        { type: "confirm" }
      );
      if (!confirmed) return;
    }
  
    // 🛠️ 7. Budujemy paczkę do zapisu
    const summary = [];
    const bulkData = [];
  
    for (const { spot, dates } of Object.values(assignments)) {
      bulkData.push(...dates.map(date => ({
        parkingSpotId: spot.id,
        date,
        regCarNum
      })));
      summary.push(`#${spot.spotNumber} → ${formatDateList(dates)}`);
    }
  
    // 🔥 8. Jeśli nie ma czego rezerwować – zakończ
    if (bulkData.length === 0) {
      showModal("❌ Brak dostępnych rezerwacji do zapisania.", { type: "error" });
      return;
    }
  
    // ✅ 9. Wysyłka do backendu
    await axios.post(`${baseUrl}/api/reservations/bulk`, {
      userId,
      reservations: bulkData
    });
  
    // ✅ 10. Pokazujemy podsumowanie rezerwacji
    const resultText = [
      `✅ Rezerwacje dokonane:\n${summary.join('\n')}`,
      unavailableDates.length ? `❌ Nie udało się zarezerwować: ${formatDateList(unavailableDates)}` : null,
      datesAlreadyMine.length ? `🔵 Już miałeś: ${formatDateList(datesAlreadyMine)}` : null
    ].filter(Boolean).join('\n\n');
  
    showModal(resultText, { type: "success" });
  
    // ♻️ 11. Odświeżenie danych
    setCalendarKey(prev => prev + 1);
    refetchUserReservations();
    if (isAffectingNextWorkingDay(selectedDates)) {
      refetchNextReservationInfo();
    }
    lightReset();
  };
  
  
  const makeReservation = useCallback(async () => {
    if (typeof userId !== "number") {
      showModal("Nie można pobrać danych użytkownika.", { type: "error" });
      return;
    }

    if (!regCarNum) {
      await showModal("Najpierw przypisz numer rejestracyjny auta w zakładce 'Twój Profil'", { type: "error" });
      navigate("/user-panel"); // Przekierowanie po zamknięciu modala
      return;
    }

    if (reserveBtnRef.current) {
      reserveBtnRef.current.classList.add("animate");
      setTimeout(() => reserveBtnRef.current?.classList.remove("animate"), 400);
    }

    try {
      if (autoAssignEnabled) {
        await makeAutoReservation();
      } else {
        await makeManualReservation();
      }
    } catch (err) {
      console.error(err);
      showModal("❌ Wystąpił błąd podczas rezerwacji.", { type: "error" });
    }
  }, [
    userId,
    selectedDates,
    selectedSpot,
    regCarNum,
    baseUrl,
    reserveBtnRef,
    userReservedDates,
    refetchUserReservations,
    refetchNextReservationInfo,
    setCalendarKey,
    lightReset,
    autoAssignEnabled,
    navigate
  ]);

  return makeReservation;
}

export default useMakeReservation;
